import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/authContext';
import createAxiosInstance from '../../../utils/axiosConfig';
import { getUserManagementColumns } from '../../../utils/constants';
import AdminTables from '../../common/adminTables';
import { Alert as AlertType } from '../../../utils/interfaces';
import adminConfirmation from '../../common/modal/modalBody/adminConfirmation';
import { ConfirmationModal } from '../../common/modal';
import AdminLayout from '../../common/adminLayout';
import AddUserIcon from '../../../assets/svg/addUserIcon';
import RegisterCustomers from './registerCustomers';

const ManageSubAccounts = () => {
    const navigate = useNavigate();
    const { userData, token, logout, login } = useAuth();
    const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
    const [selectedRowData, setSelectedRowData] = useState<any>(null);

	const [formData, setFormData] = useState<{
		results: [];
		total_count: number;
	}>({ results: [], total_count: 0 });
    const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(3);
    const [isLoading, setIsLoading] = useState(true);
    const [alert, setAlert] = useState<AlertType | null>(null);
	const [showModal, setShowModal] = useState(false);
	const [selectedAction, setSelectedAction] = useState<string | null>(null);
	const [selectedAccountType, setSelectedAccountType] = useState<number | null>(null);
	const [triggerAccountTypeChange, setTriggerAccountTypeChange] = useState(false);
	const [showRegisterSideScreen, setShowRegisterSideScreen] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [editUserData, setEditUserData] = useState<any>(null);

	// useEffect to trigger handleModalConfirm when account type change is ready
	useEffect(() => {
		if (triggerAccountTypeChange && selectedAction === "changeAccountType" && selectedRowId && selectedAccountType !== null) {
			handleModalConfirm();
			setTriggerAccountTypeChange(false);
		}
	}, [triggerAccountTypeChange, selectedAction, selectedRowId, selectedAccountType]);

    // User Action Handlers
    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const action = e.target.value;
		if (action) {
			if (action === "visitAccount") {
				login_to_customer_account();
			}else{
				setSelectedAction(action);
				setShowModal(true);
			}
		}
	};

	const login_to_customer_account = async () => {
		try{
			const response = await createAxiosInstance().post("/login-to-customer", {
				email: selectedRowData.email,
			});
			store_parent_data(
				JSON.parse(localStorage.getItem(
					'userData') || "{}"), 
					localStorage.getItem('token') || ""
				);
			login(response.data.id, response.data.access_token, {...response.data, is_customer_account: true});
		}catch(error: any){
			console.log(error, "error")
		}
		// navigate("/");
	}

	const store_parent_data = (userData: any, token: string )=>{
		localStorage.setItem("parent_data", JSON.stringify({...userData}))
		localStorage.setItem("parent_token", token || "");
		localStorage.setItem("parent_id", userData.id);
	}

    const handleRowSelection = (rowData: any) => {
		if (rowData?.id !== selectedRowId) {
			setSelectedRowId(rowData?.id);
			setSelectedRowData(rowData);
			console.log(rowData, "Row data")
		} else {
			setSelectedRowId(null);
			setSelectedRowData(null);
		}
	};

    // Common error handling function
    const handleApiError = (error: any, context: string = "API request") => {
        if (error.status === 401 || error.status === 403) {
            logout();
            navigate("/");
        }
        console.error(`Error in ${context}:`, error);
        return { results: [], total_count: 0 };
    };

    // Data Grabbers
    const getCustomers = async () => {
		try {
			const response = await createAxiosInstance().get("/customers", {
				params: {
					offset: (currentPage - 1) * itemsPerPage,
					limit: itemsPerPage,
				},
			});
			return response;
		} catch (error: any) {
			return { data: handleApiError(error, "fetching customers") };
		}
	};

    const getFormData = async () => {
		try {
			setIsLoading(true);
			setFormData({ results: [], total_count: 0 });
			const response = await getCustomers();
			console.log(response.data, "response");
			setFormData(response.data);
		} catch (error: any) {
			handleApiError(error, "loading form data");
		} finally {
			setIsLoading(false);
		}
	};

    useEffect(() => {
		getFormData();
	}, [currentPage, itemsPerPage]);

	useEffect(() => {
		if (userData && userData?.account_type < 3) {
			navigate("/");
		}
	}, [userData]);

	// Unified action configuration
	const actionConfig: Record<string, { endpoint: string, resultMessage: string }> = {
		"changeAccountType": { endpoint: "/update-customer-account-access", resultMessage: "updated" },
		"delete": { endpoint: "/delete-customer", resultMessage: "deleted" },
		"suspend": { endpoint: "/suspend-customer", resultMessage: "suspended" },
		"reactivate": { endpoint: "/enable-customer", resultMessage: "reactivated" },
		'visitAccount': {endpoint: "/visit-account", resultMessage: "visited"}
	};

	const getActionEndpoint = (action: string): string => {
		return actionConfig[action]?.endpoint || `/users/${action}`;
	};
	// Helper function to build the payload
	const buildActionPayload = (action: string): Record<string, any> => {
		if (action === "changeAccountType") {
			return {
				new_account_type: selectedAccountType,
				user_id: selectedRowId,
			};
		}
		
		return { user_id: selectedRowId };
	};
	const getSuccessMessage = (action: string): string => {
		const resultMessage = actionConfig[action]?.resultMessage || action;
		return `Account with id ${selectedRowId} successfully ${resultMessage}`;
	};

	const handleModalConfirm = async () => {
		if (!selectedAction) return;
		
		try {
			const url = getActionEndpoint(selectedAction);
			const payload = buildActionPayload(selectedAction);
			
			await createAxiosInstance().put(url, payload);
			handleAlert(getSuccessMessage(selectedAction), "success");
			getFormData();
			setSelectedRowId(null);
		} catch (error: any) {
			handleApiError(error, "Error performing action");
		} finally {
			setShowModal(false);
			setSelectedAction(null);
		}
	};

	const handleModalCancel = () => {
		setShowModal(false);
		setSelectedAction(null);
	};

	const handleAlert = (message: string, type: "success" | "danger") => {
		setAlert({
			message,
			type,
			show: true
		});
	};

	// Action panel component
	const ActionPanel = () => (
		<>
			{selectedRowId && (
				<>
					<label htmlFor="floatingSelect">User Actions:</label>
					<select
						onChange={handleSelectChange}
						className="form-select mb-4"
						id="floatingSelect"
						aria-label="Floating label select example"
					>
						<option value="">Select an action</option>
						<option value="delete">Delete</option>
						{selectedRowData?.is_enabled && <option value="suspend">Suspend</option>}
						{!selectedRowData?.is_enabled && <option value="reactivate">Reactivate</option>}
						
						<option value="changeAccountType">
							Change Account Type
						</option>
						<option value="visitAccount">Visit Account</option>
					</select>
				</>
			)}
		</>
	);

	const handleAddNewUser = () => {
		setIsEditMode(false);
		setEditUserData(null);
		setShowRegisterSideScreen(true);
	};

	const handleCloseRegisterSideScreen = () => {
		setShowRegisterSideScreen(false);
		setIsEditMode(false);
		setEditUserData(null);
	};

	const handleRegistrationSuccess = () => {
		// Refresh the table data
		getFormData();
		// Close the side screen
		setShowRegisterSideScreen(false);
		setIsEditMode(false);
		setEditUserData(null);
		// Show a success message
		const message = isEditMode ? "Customer updated successfully!" : "Customer registered successfully!";
		handleAlert(message, "success");
	};

	// Define your action handlers in the parent component
	const handleUserSettings = (user: any) => {
		console.log("Opening settings for user:", user.id);
		setIsEditMode(true);
		setEditUserData(user);
		setShowRegisterSideScreen(true);
	};

	const handleToggleUserBan = (user: any) => {
		console.log(user.is_enabled ? "Banning user:" : "Unbanning user:", user.id);
		setSelectedAction(user.is_enabled ? "suspend" : "reactivate");
		setSelectedRowId(user.id);
		setSelectedRowData(user);
		setShowModal(true);
	};

	const handleDeleteUser = (user: any) => {
		setSelectedAction("delete");
		setSelectedRowId(user.id);
		setSelectedRowData(user);
		setShowModal(true);
	};

	// Create columns with your action handlers
	const columns = getUserManagementColumns({
		onSettings: handleUserSettings,
		onToggleBan: handleToggleUserBan,
		onDelete: handleDeleteUser,
		onFullNameClick: (user: any) => {
			// Set the selected row data and call login_to_customer_account
			setSelectedRowData(user);
			login_to_customer_account();
		}
	});

	return (
		<AdminLayout
			title="Manage Customers"
			// actionPanel={<ActionPanel />}
			formData={formData}
			currentPage={currentPage}
			setCurrentPage={setCurrentPage}
			itemsPerPage={itemsPerPage}
			setItemsPerPage={setItemsPerPage}
			alert={alert || undefined}
			setAlert={setAlert}
		>
			<div className='d-flex justify-content-end'>
				<button 
					className="btn btn-rounded-blue d-flex align-items-center gap-2" 
					onClick={handleAddNewUser}
					style={{
						width: '13.8%',
						borderRadius: '77.125rem'
					}}
				>
					<AddUserIcon />
					ADD NEW USER
				</button>
			</div>
			<AdminTables
				containerStyle={{
					width: "100%",
					overflowX: "auto",
					marginTop: '0.8rem'
				}} 
				data={formData.results}
				columns={columns}
				rowSelectionEnabled={false}
				onRowSelect={handleRowSelection}
				showSearch={false}
				isLoading={isLoading}
			/>
			<ConfirmationModal
				show={showModal}
				onClose={handleModalCancel}
				onConfirm={handleModalConfirm}
				title={selectedAction === "delete" ? "Delete Confirmation" : "Suspend Account Confirmation"}
				body={adminConfirmation({ 
					action: selectedAction ?? "",
					selectedAccountType,
					setSelectedAccountType })}
				maxHeight={selectedAction === "changeAccountType" ? "30vh" : "34vh"}
				maxWidth={'43.22vw'}
			/>

			{/* Register Customer Side Screen */}
			{showRegisterSideScreen && (
				<>
					<div
						className="offcanvas-backdrop fade show"
						onClick={handleCloseRegisterSideScreen}
					></div>
					<div
						className="offcanvas offcanvas-end show"
						tabIndex={-1}
						style={{ 
							zIndex: 1050,
							// width: '55%',
							
						}}
					>
						<div className="offcanvas-header">
							<h5 className="offcanvas-title" style={{
								fontSize: '1.25rem',
								fontWeight: 'bold',
								marginLeft: '1rem',
								padding: '10px 0px'
							}}>
								{isEditMode ? "Update Customer" : "Add New Customer"}
							</h5>
							<button
								type="button"
								className="btn-close"
								aria-label="Close"
								onClick={handleCloseRegisterSideScreen}
							></button>
						</div>
						<div className="offcanvas-body px-4">
							<RegisterCustomers 
								onSuccess={handleRegistrationSuccess}
								isUpdateMode={isEditMode}
								userData={editUserData}
								updateEndpoint="/update-customer-account"
							/>
						</div>
					</div>
				</>
			)}
		</AdminLayout>
	);
};

export default ManageSubAccounts;
